import { AxiosError, AxiosResponse } from 'axios';
import { axiosInstance } from '@fides/fides-react-components';
import { CreditTransferOrder, Transaction, TransactionGroup } from '../models';
import { JsonObject } from '../types';
import { endpoints } from '../consts';
import { handleDocumentGenerationError } from './Grid';

export const orderAction = (
  action: string,
  uniqueIds: CreditTransferOrder['uniqueId'] | CreditTransferOrder['uniqueId'][],
  code = ''
): Promise<void> => {
  const ids = Array.isArray(uniqueIds) ? uniqueIds : [uniqueIds];

  return axiosInstance
    .post(
      `${endpoints.orders}/${action}`,
      action === 'sign'
        ? {
            uuids: ids,
            securityCode: code,
          }
        : ids,
      { baseURL: endpoints.EFTApiUrl }
    )
    .then(() => Promise.resolve())
    .catch((error: AxiosError<JsonObject>) => Promise.reject(error));
};

export const createOrder = (data): Promise<AxiosResponse<CreditTransferOrder>> =>
  axiosInstance({
    baseURL: endpoints.EFTApiUrl,
    url: endpoints.orders,
    method: 'POST',
    data,
  });

export const generateOrder = (): Promise<AxiosResponse<CreditTransferOrder>> =>
  axiosInstance({
    baseURL: endpoints.EFTApiUrl,
    url: `${endpoints.orders}/generate`,
    method: 'POST',
    data: {},
  });

export const updateOrder = (
  uniqueId: string,
  payload: CreditTransferOrder
): Promise<AxiosResponse<CreditTransferOrder>> =>
  axiosInstance.put(`${endpoints.orders}/${uniqueId}`, payload, {
    baseURL: endpoints.EFTApiUrl,
  });

export const handleGenerateXML = (orderUniqueId: string, snackbar: any, t: any): void => {
  axiosInstance({
    baseURL: endpoints.EFTApiUrl,
    url: `${endpoints.orders}/${orderUniqueId}/${endpoints.getXml}`,
    method: 'PUT',
  })
    .then(() => snackbar(t('payments.generateReport.DOCUMENT_REQUEST_PENDING'), 'info'))
    .catch((error) => {
      handleDocumentGenerationError(
        error,
        snackbar,
        t as (key: string, options?: any) => string,
        'payments.generateReport.DOCUMENT_REQUEST_FAILED'
      );
    });
};

type GenerateTxResponse = Transaction | Transaction[] | { transactions: Transaction[] };

export const generateTransaction = (
  numberOfNewTransactions: number,
  transactionGroupUniqueId: string
): Promise<AxiosResponse<GenerateTxResponse>> =>
  axiosInstance({
    baseURL: endpoints.EFTApiUrl,
    url: endpoints.generateTransaction,
    method: 'GET',
    params: {
      numberOfNewTransactions,
      transactionGroupUniqueId,
    },
  });

export const generateTransactionGroup = (index: number, orderID?: string): Promise<AxiosResponse<TransactionGroup>> =>
  axiosInstance({
    baseURL: endpoints.EFTApiUrl,
    url: endpoints.generateTransactionGroups,
    method: 'GET',
    params: {
      index,
      orderID,
    },
  });

export const generateFromTemplate = (templateId: string): Promise<AxiosResponse<CreditTransferOrder>> =>
  axiosInstance({
    baseURL: endpoints.EFTApiUrl,
    url: endpoints.generateFromTemplate,
    method: 'POST',
    headers: {
      'Content-Type': 'text/plain',
    },
    data: templateId,
  });

export const uploadTemplateValuesFile = (file: File): Promise<AxiosResponse<any>> => {
  const formData = new FormData();
  formData.append('file', file);
  return axiosInstance.post(endpoints.fromTemplateValues, formData, {
    baseURL: endpoints.EFTApiUrl,
  });
};
